<template>
  <div v-if="isFetching" class="text-center py-4">
    <i class="fa fa-spinner fa-spin fa-2x"></i>
    <p class="mt-2">Memuat data...</p>
  </div>

  <div v-else>
    <form @submit.prevent="save">
      <!-- Nama Level -->
      <div class="form-group mb-3">
        <label>Nama Level <span class="text-danger">*</span></label>
        <input
          v-model="form.nama_level"
          type="text"
          class="form-control"
          placeholder="Masukkan Nama Level"
          required
        />
      </div>

      <!-- Deskripsi Perilaku -->
      <div class="form-group mb-3">
        <label>Deskripsi Perilaku</label>
        <input
          v-model="form.deskripsi_perilaku"
          type="text"
          class="form-control"
          placeholder="Masukkan Deskripsi Perilaku"
        />
      </div>

      <!-- Taksonomi Bloom -->
      <div class="form-group mb-3">
        <label>Taksonomi Bloom</label>
        <table class="table table-condensed">
          <tbody>
            <tr v-for="(taksonomi, index) in form.taksonomi" :key="index">
              <td>
                <v-select
                  v-model="form.taksonomi[index]"
                  :options="availableOptions(index)"
                  :filterable="false"
                  placeholder="Cari Taksonomi"
                  label="kode"
                  @search="onSearchTaksonomi"
                >
                  <template #selected-option="{ kode, level_kognitif }">
                    {{ kode }} - {{ level_kognitif }}
                  </template>
                  <template #option="option">
                    {{ option.kode }} - {{ option.level_kognitif }}
                  </template>
                  <template #no-options="{ search, searching, loading }">
                    <span v-if="loading">Mencari...</span>
                    <span v-else-if="searching">
                      Tidak ada hasil untuk "<strong>{{ search }}</strong
                      >"
                    </span>
                    <span v-else>Ketik untuk mencari taksonomi</span>
                  </template>
                </v-select>
              </td>
              <td width="50">
                <button
                  type="button"
                  class="btn btn-danger"
                  @click="removeTaksonomi(index)"
                >
                  <i class="fa fa-trash"></i>
                </button>
              </td>
            </tr>
            <tr>
              <td colspan="2">
                <div class="d-grid gap-2">
                  <button
                    type="button"
                    class="btn btn-primary"
                    @click="addTaksonomi"
                  >
                    <i class="fa fa-plus me-1"></i> Tambah Taksonomi Bloom
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Action Buttons -->
      <div class="text-end mt-4">
        <button
          type="button"
          class="btn btn-light me-1"
          @click="$emit('close')"
        >
          Tutup
        </button>
        <button type="submit" class="btn btn-success" :disabled="isLoading">
          <span v-if="isLoading">
            <i class="fa fa-spinner fa-spin me-1"></i> Menyimpan...
          </span>
          <span v-else>Simpan</span>
        </button>
      </div>
    </form>
  </div>
</template>
<script>
import axios from "axios";
import { _ } from "core-js";
import Swal from "sweetalert2";

export default {
  name: "FormLevelKompetensi",

  props: {
    selected_id: {
      type: [Number, String],
      default: null,
    },
  },

  emits: ["close", "saved"],

  data() {
    return {
      isLoading: false,
      isFetching: false, // loading saat fetch detail
      filterData: null,
      form: {
        nama_level: "",
        deskripsi_perilaku: "",
        taksonomi: [],
      },
      option_taksonomi: [],
    };
  },

  watch: {
    // Setiap kali selected_id berubah (modal dibuka ulang), reset/fetch ulang
    selected_id: {
      immediate: true,
      handler(id) {
        if (id) {
          this.fetchDetail(id);
        } else {
          this.resetForm();
        }
        this.searchTaksonomi();
      },
    },
  },

  mounted() {
    this.filterData = _.debounce((search, loading) => {
      if (search?.length) {
        this.searchTaksonomi(search, loading);
      }
    }, 500);
  },

  methods: {
    // ─── Fetch Detail untuk Edit ──────────────────────────────────
    async fetchDetail(id) {
      this.isFetching = true;
      try {
        const url =
          process.env.VUE_APP_BACKEND_URL_VERSION +
          `verifikasi/level-kompetensi/${id}`;

        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${localStorage.access_token}` },
        });

        const data = response.data.data;

        // Sesuaikan key dengan struktur response BE
        this.form = {
          nama_level: data.nama_level ?? "",
          deskripsi_perilaku: data.deskripsi_perilaku ?? "",
          // Asumsikan BE mengembalikan array of taksonomi object
          taksonomi: data.taksonomi ?? [],
        };

        // Pastikan taksonomi dari detail juga masuk ke option
        // agar v-select bisa menampilkan label dengan benar
        this.mergeDetailTaksonomiToOptions(data.taksonomi ?? []);
      } catch (error) {
        console.error("Gagal memuat detail:", error);
        Swal.fire({
          icon: "error",
          title: "Gagal memuat data",
          text: error.response?.data?.data ?? "Terjadi kesalahan",
        });
      } finally {
        this.isFetching = false;
      }
    },

    // Merge taksonomi dari detail ke option_taksonomi
    // agar item yang sudah dipilih tetap muncul di dropdown
    mergeDetailTaksonomiToOptions(detailTaksonomi) {
      const existingIds = new Set(this.option_taksonomi.map((o) => o.id));
      detailTaksonomi.forEach((t) => {
        if (!existingIds.has(t.id)) {
          this.option_taksonomi.push(t);
        }
      });
    },

    // ─── Taksonomi ────────────────────────────────────────────────
    availableOptions(currentIndex) {
      const selectedIds = this.form.taksonomi
        .map((t, i) => (i !== currentIndex && t ? t.idverif_taksonomi : null))
        .filter(Boolean);

      return this.option_taksonomi.filter(
        (opt) => !selectedIds.includes(opt.idverif_taksonomi),
      );
    },

    onSearchTaksonomi(search, loading) {
      if (!search?.length) return;
      loading(true);
      this.filterData(search, loading);
    },

    async searchTaksonomi(search = "", loading = null) {
      try {
        const url =
          process.env.VUE_APP_BACKEND_URL_VERSION + "referensi/taksonomi/all";
        const response = await axios.get(url, {
          params: { search, isLevel: 0 },
          headers: { Authorization: `Bearer ${localStorage.access_token}` },
        });

        const data = response.data.data.data;
        this.option_taksonomi = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error("Gagal memuat taksonomi:", error);
        this.option_taksonomi = [];
      } finally {
        if (loading) loading(false);
      }
    },

    addTaksonomi() {
      this.form.taksonomi.push(null);
    },

    removeTaksonomi(index) {
      this.form.taksonomi.splice(index, 1);
    },
    
    async save() {
      if (!this.form.nama_level.trim()) {
        alert("Nama Level wajib diisi.");
        return;
      }

      this.isLoading = true;
      try {
        const url =
          process.env.VUE_APP_BACKEND_URL_VERSION +
          "verifikasi/level-kompetensi";

        const payload = {
          nama_level: this.form.nama_level,
          deskripsi_perilaku: this.form.deskripsi_perilaku,
          taksonomi: this.form.taksonomi
            .filter(Boolean)
            .map((t) => t.idverif_taksonomi),
        };

        const method = this.selected_id ? "put" : "post";
        const endpoint = this.selected_id ? `${url}/${this.selected_id}` : url;

        const response = await axios({
          method,
          url: endpoint,
          data: payload,
          headers: { Authorization: `Bearer ${localStorage.access_token}` },
        });

        await Swal.fire({
          icon: "success",
          title: "Berhasil",
          text: response.data?.meta?.message ?? "Data berhasil disimpan",
        });

        this.$emit("saved");
        this.resetForm();
      } catch (error) {
        console.error("Gagal menyimpan data:", error);

        const metaMessage = error?.response?.data?.meta?.message;
        const validationErrors = error?.response?.data?.data ?? [];

        const isValidationError =
          metaMessage === "Validation failed" &&
          Array.isArray(validationErrors) &&
          validationErrors.length > 0;

        const message = isValidationError
          ? `<ul style="text-align:left;">
           ${validationErrors.map((err) => `<li>${err}</li>`).join("")}
         </ul>`
          : metaMessage ?? "Terjadi kesalahan";

        Swal.fire({
          icon: "error",
          title: "Oops...",
          html: isValidationError ? message : undefined,
          text: isValidationError ? undefined : message,
        });
      } finally {
        this.isLoading = false;
      }
    },

    resetForm() {
      this.form = {
        nama_level: "",
        deskripsi_perilaku: "",
        taksonomi: [],
      };
    },
  },
};
</script>
